import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCircle, 
  AlertCircle, 
  Loader, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  Clock,
  FileText,
  Shield,
  ChevronRight,
  ChevronLeft,
  CalendarCheck,
  XCircle,
  FileSignature,
  Eraser,
  Check
} from 'lucide-react';

// ============================================================================
// QUOTE ACCEPT FORM - Customer-facing quote view and acceptance
// Accessed via tokenized link from quote email
// ============================================================================

const QuoteAcceptForm = ({ token, onAccepted }) => {
  // -------------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------------
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  
  // Quote status from API
  const [isExpired, setIsExpired] = useState(false);
  const [isAlreadyAccepted, setIsAlreadyAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState(null);
  
  // Form state - Step 1: Date Selection
  const [selectedDate, setSelectedDate] = useState('');
  const [timePreference, setTimePreference] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  
  // Form state - Step 2: Agreement (if required)
  const [currentStep, setCurrentStep] = useState(1); // 1 = date, 2 = agreement
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signatureImage, setSignatureImage] = useState(null);
  const [photoConsentGiven, setPhotoConsentGiven] = useState(false);
  const [cancellationPolicyAccepted, setCancellationPolicyAccepted] = useState(false);
  const [weatherPolicyAccepted, setWeatherPolicyAccepted] = useState(false);
  
  // Signature pad state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  
  // -------------------------------------------------------------------------
  // API URL
  // -------------------------------------------------------------------------
  const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://suddyflow-api-cugygabvhfg6dvfj.eastus2-01.azurewebsites.net';

  // -------------------------------------------------------------------------
  // TIME PREFERENCE OPTIONS
  // -------------------------------------------------------------------------
  const timeOptions = [
    { value: '', label: 'Select a time preference...' },
    { value: 'Morning', label: 'Morning' },
    { value: 'Afternoon', label: 'Afternoon' },
    { value: 'Evening', label: 'Evening' },
    { value: 'Flexible', label: 'Flexible / No Preference' },
  ];

  // -------------------------------------------------------------------------
  // FETCH QUOTE DATA
  // -------------------------------------------------------------------------
  
  useEffect(() => {
    const fetchQuote = async () => {
      if (!token) {
        setError('Invalid quote link');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE}/api/quote/${token}`);
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.message || 'Unable to load quote');
          return;
        }
        
        setQuoteData(data.quote);
        setIsExpired(data.isExpired || false);
        setIsAlreadyAccepted(data.isAccepted || false);
        setAcceptedAt(data.acceptedAt || null);
        
      } catch (err) {
        console.error('Error fetching quote:', err);
        setError('Unable to connect. Please check your internet connection.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuote();
  }, [token, API_BASE]);

  // -------------------------------------------------------------------------
  // PRE-POPULATE DATE FROM JOB'S SCHEDULED DATE
  // -------------------------------------------------------------------------
  
  useEffect(() => {
    if (quoteData?.scheduledDate) {
      // Parse the scheduled date and format as YYYY-MM-DD for the input
      const date = new Date(quoteData.scheduledDate);
      if (!isNaN(date.getTime())) {
        const formatted = date.toISOString().split('T')[0];
        setSelectedDate(formatted);
      }
    }
  }, [quoteData]);

  // -------------------------------------------------------------------------
  // SIGNATURE PAD SETUP
  // -------------------------------------------------------------------------
  
  useEffect(() => {
    if (currentStep === 2 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      ctx.scale(2, 2);
      
      // Set drawing style
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [currentStep]);

  // -------------------------------------------------------------------------
  // SIGNATURE PAD HANDLERS
  // -------------------------------------------------------------------------
  
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.closePath();
      setIsDrawing(false);
      
      // Capture signature as base64
      if (hasSignature) {
        setSignatureImage(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureImage(null);
  };

  // -------------------------------------------------------------------------
  // STEP NAVIGATION
  // -------------------------------------------------------------------------
  
  const handleContinueToAgreement = (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      setSubmitError('Please select a date for your service.');
      return;
    }
    
    setSubmitError(null);
    setCurrentStep(2);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToDate = () => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // -------------------------------------------------------------------------
  // FORM SUBMISSION
  // -------------------------------------------------------------------------
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      setSubmitError('Please select a date for your service.');
      return;
    }
    
    // If on step 1 and agreement is required, go to step 2
    if (currentStep === 1 && quoteData?.requireAgreement && quoteData?.agreement) {
      handleContinueToAgreement(e);
      return;
    }
    
    // If on step 2 (agreement), validate signature
    if (currentStep === 2) {
      if (!agreementAccepted) {
        setSubmitError('Please check the box to accept the agreement.');
        return;
      }
      if (!signerName.trim()) {
        setSubmitError('Please enter your name.');
        return;
      }
      if (!hasSignature) {
        setSubmitError('Please sign in the signature box.');
        return;
      }
    }
    
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      // Build request body
      const requestBody = {
        selectedDate: selectedDate,
        timePreference: timePreference || null,
        customerNotes: customerNotes.trim() || null,
      };
      
      // Add signature data if signing agreement
      if (currentStep === 2 && hasSignature) {
        requestBody.signatureImage = signatureImage;
        requestBody.signerName = signerName.trim();
        requestBody.signerEmail = quoteData?.customerEmail || null;
        requestBody.photoConsentGiven = photoConsentGiven;
        requestBody.cancellationPolicyAccepted = cancellationPolicyAccepted;
        requestBody.weatherPolicyAccepted = weatherPolicyAccepted;
      }
      
      const response = await fetch(`${API_BASE}/api/quote/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to accept quote');
      }
      
      setSubmitSuccess(data);
      
      if (onAccepted) {
        onAccepted(data);
      }
      
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(err.message || 'Unable to accept quote. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // HELPERS
  // -------------------------------------------------------------------------
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  
  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Check if agreement has consent options
  const hasConsentOptions = quoteData?.agreement && (
    quoteData.agreement.includePhotoConsent ||
    quoteData.agreement.includeCancellationPolicy ||
    quoteData.agreement.includeWeatherPolicy
  );

  // Check if we need to show the agreement step
  const showAgreementStep = quoteData?.requireAgreement && quoteData?.agreement;

  // -------------------------------------------------------------------------
  // RENDER: LOADING STATE
  // -------------------------------------------------------------------------
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your quote...</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: ERROR STATE (no quote data)
  // -------------------------------------------------------------------------
  
  if (error && !quoteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Quote</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact the business directly.
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: ALREADY ACCEPTED STATE
  // -------------------------------------------------------------------------
  
  if (isAlreadyAccepted && quoteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="max-w-lg mx-auto pt-8 sm:pt-16">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Quote Already Accepted
            </h2>
            
            <p className="text-gray-600 mb-6">
              This quote was accepted{acceptedAt ? ` on ${formatDate(acceptedAt)}` : ''}.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-1">Service at:</p>
              <p className="font-medium text-gray-900">{quoteData.address}</p>
              {quoteData.city && (
                <p className="text-gray-600">{quoteData.city}, {quoteData.state} {quoteData.zipCode}</p>
              )}
            </div>
            
            {/* Contact info */}
            {quoteData.businessPhone && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-500 text-sm mb-2">Questions? Contact us:</p>
                <a 
                  href={`tel:${quoteData.businessPhone.replace(/\D/g, '')}`}
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
                >
                  <Phone className="w-5 h-5" />
                  {quoteData.businessPhone}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: EXPIRED STATE
  // -------------------------------------------------------------------------
  
  if (isExpired && quoteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4">
        <div className="max-w-lg mx-auto pt-8 sm:pt-16">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-amber-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Quote Has Expired
            </h2>
            
            <p className="text-gray-600 mb-6">
              This quote expired on {formatDate(quoteData.expiresAt)}. 
              Please contact us for updated pricing.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Original quote for:</p>
              <p className="font-medium text-gray-900">{quoteData.address}</p>
              <p className="text-2xl font-bold text-gray-400 mt-2 line-through">
                {formatCurrency(quoteData.totalAmount)}
              </p>
            </div>
            
            {/* Contact options */}
            <div className="space-y-3">
              {quoteData.businessPhone && (
                <a 
                  href={`tel:${quoteData.businessPhone.replace(/\D/g, '')}`}
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Call for New Quote
                </a>
              )}
              
              {quoteData.businessEmail && (
                <a 
                  href={`mailto:${quoteData.businessEmail}?subject=Quote Request - ${quoteData.address}`}
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Email Us
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: SUCCESS STATE
  // -------------------------------------------------------------------------
  
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="max-w-lg mx-auto pt-8 sm:pt-16">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CalendarCheck className="w-12 h-12 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              You're All Set! 🎉
            </h2>
            
            <p className="text-gray-600 mb-6">
              {submitSuccess.message || "Your service has been scheduled. We'll contact you to confirm the exact time."}
            </p>
            
            {/* Scheduled date card */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-6 mb-6">
              <p className="text-green-100 text-sm mb-1">Scheduled For</p>
              <p className="text-xl sm:text-2xl font-bold">
                {formatDate(submitSuccess.scheduledDate)}
              </p>
              {submitSuccess.timePreference && submitSuccess.timePreference !== 'Flexible' && (
                <p className="text-green-100 mt-1">
                  {submitSuccess.timePreference}
                </p>
              )}
            </div>
            
            {/* Agreement signed confirmation */}
            {submitSuccess.agreementSigned && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center gap-2 text-purple-700">
                  <FileSignature className="w-5 h-5" />
                  <span className="font-semibold">Service Agreement Signed</span>
                </div>
                <p className="text-sm text-purple-600 mt-1">
                  Reference: AGR-{String(submitSuccess.signedAgreementId).padStart(6, '0')}
                </p>
              </div>
            )}
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-1">Service Location</p>
              <p className="font-medium text-gray-900">{quoteData.address}</p>
              {quoteData.city && (
                <p className="text-gray-600">{quoteData.city}, {quoteData.state} {quoteData.zipCode}</p>
              )}
            </div>
            
            <div className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              <span>We'll call you to confirm the exact time</span>
            </div>
            
            {/* Contact info */}
            {quoteData.businessPhone && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-500 text-sm mb-2">Questions? Reach us at:</p>
                <a 
                  href={`tel:${quoteData.businessPhone.replace(/\D/g, '')}`}
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
                >
                  <Phone className="w-5 h-5" />
                  {quoteData.businessPhone}
                </a>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <p className="text-center text-gray-400 text-sm mt-6">
            Powered by <a href="https://suddyflow.com" className="text-blue-500 hover:underline" target="_blank" rel="noopener">SuddyFlow</a>
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: MAIN QUOTE VIEW & ACCEPTANCE FORM
  // -------------------------------------------------------------------------
  
  const brandColor = quoteData?.brandColor || '#7c3aed';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      
      {/* ========== HEADER ========== */}
      <header 
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${brandColor} 0%, ${brandColor}ee 100%)` }}
      >
        {/* Decorative circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10 bg-white" />
          <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full opacity-10 bg-white" />
        </div>
        
        <div className="relative max-w-2xl mx-auto px-4 py-6 sm:py-8 text-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Logo / Business Name */}
            <div className="flex items-center gap-4">
              {quoteData?.logoUrl ? (
                <img 
                  src={quoteData.logoUrl} 
                  alt={quoteData.businessName}
                  className="h-36 sm:h-40 md:h-48 w-auto object-contain drop-shadow-2xl"
                />
              ) : (
                <h1 className="text-2xl sm:text-3xl font-bold drop-shadow-md">
                  {quoteData?.businessName}
                </h1>
              )}
            </div>
            
            {/* Phone */}
            {quoteData?.businessPhone && (
              <a 
                href={`tel:${quoteData.businessPhone.replace(/\D/g, '')}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white font-semibold transition-all border border-white/30"
              >
                <Phone className="w-4 h-4" />
                <span>{quoteData.businessPhone}</span>
              </a>
            )}
          </div>
          
          {/* Page title */}
          <div className="text-center mt-6">
            <h2 className="text-xl sm:text-2xl font-bold">
              {currentStep === 2 ? 'Almost Done!' : 'Your Quote is Ready'}
            </h2>
            <p className="text-white/80 mt-1">
              {currentStep === 2 ? 'Please review and sign the service agreement' : 'Review and schedule your service'}
            </p>
          </div>
          
          {/* Step indicator (if agreement required) */}
          {showAgreementStep && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                currentStep === 1 ? 'bg-white/30 font-semibold' : 'bg-white/10'
              }`}>
                <span className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-xs">1</span>
                Schedule
              </div>
              <ChevronRight className="w-4 h-4 opacity-50" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                currentStep === 2 ? 'bg-white/30 font-semibold' : 'bg-white/10'
              }`}>
                <span className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-xs">2</span>
                Agreement
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ========== MAIN CONTENT ========== */}
      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-8 -mt-4">
        
        {/* ========== STEP 1: QUOTE DETAILS & DATE SELECTION ========== */}
        {currentStep === 1 && (
          <>
        {/* Quote Summary Card */}
        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${brandColor}15` }}
            >
              <FileText className="w-5 h-5" style={{ color: brandColor }} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Quote for {quoteData?.customerName}</h3>
              <p className="text-sm text-gray-500">
                Valid until {formatDate(quoteData?.expiresAt)}
              </p>
            </div>
          </div>
          
          {/* Service Location */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl mb-4">
            <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">{quoteData?.address}</p>
              {quoteData?.city && (
                <p className="text-sm text-gray-600">
                  {quoteData.city}, {quoteData.state} {quoteData.zipCode}
                </p>
              )}
            </div>
          </div>
          
          {/* Proposed Date - Show if job has a scheduled date */}
          {quoteData?.scheduledDate && (
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl mb-4 border border-blue-100">
              <Calendar className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-600 font-medium">Proposed Service Date</p>
                <p className="font-semibold text-blue-900">{formatDate(quoteData.scheduledDate)}</p>
                <p className="text-xs text-blue-600 mt-1">You can change this below if needed</p>
              </div>
            </div>
          )}
          
          {/* Services Breakdown */}
          {quoteData?.services && quoteData.services.length > 0 && (
            <div className="border-t border-gray-100 pt-4 mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Services</h4>
              <div className="space-y-3">
                {quoteData.services.map((service, index) => {
                  // Parse breakdown to get measurement and modifiers
                  let measurementText = '';
                  let modifiers = [];
                  
                  if (service.breakdown) {
                    try {
                      const breakdown = typeof service.breakdown === 'string' 
                        ? JSON.parse(service.breakdown) 
                        : service.breakdown;
                      
                      // Get measurement value (e.g., "1,700 sq ft")
                      const measurementValue = breakdown.measurementValue ?? breakdown.MeasurementValue;
                      const measurementLabel = breakdown.measurementLabel ?? breakdown.MeasurementLabel;
                      const pricingTemplate = breakdown.pricingTemplate ?? breakdown.PricingTemplate;
                      
                      if (measurementValue && measurementValue > 0) {
                        // Determine unit label - use measurementLabel or derive from pricingTemplate
                        let unit = measurementLabel || '';
                        if (!unit && pricingTemplate) {
                          unit = {
                            'per-sqft': 'sq ft',
                            'per-linear-ft': 'linear ft',
                            'per-unit': 'units',
                            'per-hour': 'hours',
                          }[pricingTemplate] || '';
                        }
                        if (unit) {
                          measurementText = `${Number(measurementValue).toLocaleString()} ${unit}`;
                        }
                      }
                      
                      // Get selected tier if tiered pricing
                      const selectedTier = breakdown.selectedTier ?? breakdown.SelectedTier;
                      if (selectedTier && !measurementText) {
                        measurementText = selectedTier;
                      }
                      
                      // Get active modifiers - handle both array and dictionary formats
                      const activeModifiers = breakdown.activeModifiers ?? breakdown.ActiveModifiers;
                      if (activeModifiers) {
                        if (Array.isArray(activeModifiers)) {
                          // Array format: [{ name: "2-Story", amount: 100 }]
                          modifiers = activeModifiers
                            .map(m => m.name || m.Name)
                            .filter(Boolean);
                        } else if (typeof activeModifiers === 'object') {
                          // Dictionary format: { "2-Story": true }
                          modifiers = Object.entries(activeModifiers)
                            .filter(([_, isActive]) => isActive)
                            .map(([name]) => name);
                        }
                      }
                    } catch (e) {
                      console.error('Failed to parse service breakdown:', e);
                    }
                  }
                  
                  return (
                    <div key={index} className="py-2 border-b border-gray-100 last:border-0">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {index + 1}. {service.name}
                            {measurementText && (
                              <span className="text-gray-600 font-normal"> ({measurementText})</span>
                            )}
                          </p>
                          {/* Show modifiers/add-ons */}
                          {modifiers.length > 0 && (
                            <div className="mt-1 ml-4">
                              {modifiers.map((mod, modIndex) => (
                                <p key={modIndex} className="text-sm text-gray-500">
                                  • {mod} included
                                </p>
                              ))}
                            </div>
                          )}
                          {/* Show notes only if no modifiers */}
                          {service.notes && modifiers.length === 0 && (
                            <p className="text-sm text-gray-500 mt-1 ml-4">• {service.notes}</p>
                          )}
                        </div>
                        <p className="font-semibold text-gray-900 ml-4">
                          {formatCurrency(service.total)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Total */}
          <div 
            className="flex justify-between items-center p-4 rounded-xl"
            style={{ backgroundColor: `${brandColor}10` }}
          >
            <span className="font-semibold text-gray-900">Total</span>
            <span 
              className="text-2xl sm:text-3xl font-bold"
              style={{ color: brandColor }}
            >
              {formatCurrency(quoteData?.totalAmount)}
            </span>
          </div>
          
          {/* Job description if present */}
          {quoteData?.description && (
            <div className="mt-4 p-3 bg-blue-50 rounded-xl">
              <p className="text-sm text-blue-800">{quoteData.description}</p>
            </div>
          )}
          
          {/* Agreement notice */}
          {showAgreementStep && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-xl">
              <div className="flex items-center gap-2 text-purple-700">
                <FileSignature className="w-5 h-5" />
                <span className="font-medium">Service agreement required</span>
              </div>
              <p className="text-sm text-purple-600 mt-1">
                You'll review and sign our service agreement in the next step.
              </p>
            </div>
          )}
        </div>

        {/* Schedule Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" style={{ color: brandColor }} />
              {quoteData?.scheduledDate ? 'Confirm or Change Service Date' : 'Choose Your Service Date'}
            </h3>
            
            {/* Date Picker */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={getMinDate()}
                required
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all text-lg"
              />
              <p className="text-xs text-gray-500 mt-1">
                {quoteData?.scheduledDate 
                  ? 'The proposed date is pre-selected. Change it if you prefer a different date.'
                  : 'Select your preferred service date'
                }
              </p>
            </div>
            
            {/* Time Preference */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Preference
              </label>
              <select
                value={timePreference}
                onChange={(e) => setTimePreference(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all text-lg appearance-none bg-white"
              >
                {timeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                We'll call you to confirm the exact time
              </p>
            </div>
            
            {/* Customer Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                rows={2}
                placeholder="Gate code, parking info, special instructions..."
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all resize-none"
                maxLength={500}
              />
            </div>
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Unable to Submit</p>
                <p className="text-sm text-red-700">{submitError}</p>
                {quoteData?.businessPhone && (
                  <p className="text-sm text-red-700 mt-1">
                    Please call us at{' '}
                    <a 
                      href={`tel:${quoteData.businessPhone.replace(/\D/g, '')}`} 
                      className="font-semibold underline"
                    >
                      {quoteData.businessPhone}
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !selectedDate}
            className="w-full py-4 px-6 rounded-xl font-bold text-lg text-white transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg flex items-center justify-center gap-2"
            style={{ 
              background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)`,
            }}
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : showAgreementStep ? (
              <>
                Continue to Agreement
                <ChevronRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Accept & Schedule
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-gray-500 text-sm">
            <span className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              Secure
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              We'll confirm your time
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              No payment required now
            </span>
          </div>
        </form>
          </>
        )}

        {/* ========== STEP 2: AGREEMENT SIGNING ========== */}
        {currentStep === 2 && quoteData?.agreement && (
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Agreement Content */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center gap-3" style={{ backgroundColor: `${brandColor}10` }}>
                <FileSignature className="w-5 h-5" style={{ color: brandColor }} />
                <h3 className="font-semibold text-gray-900">{quoteData.agreement.title}</h3>
              </div>
              
              {/* Scrollable agreement text */}
              <div 
                className="p-5 max-h-80 overflow-y-auto text-sm text-gray-700 leading-relaxed prose prose-sm"
                dangerouslySetInnerHTML={{ __html: quoteData.agreement.htmlContent }}
              />
            </div>

            {/* Consent Checkboxes (if applicable) */}
            {hasConsentOptions && (
              <div className="bg-white rounded-2xl shadow-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-4">Additional Consents</h4>
                <div className="space-y-3">
                  {quoteData.agreement.includePhotoConsent && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={photoConsentGiven}
                        onChange={(e) => setPhotoConsentGiven(e.target.checked)}
                        className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Photo/Video Consent</span>
                        <p className="text-xs text-gray-500">I consent to photos/videos of the work being used for marketing purposes</p>
                      </div>
                    </label>
                  )}
                  
                  {quoteData.agreement.includeCancellationPolicy && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cancellationPolicyAccepted}
                        onChange={(e) => setCancellationPolicyAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Cancellation Policy</span>
                        <p className="text-xs text-gray-500">I understand and accept the cancellation policy</p>
                      </div>
                    </label>
                  )}
                  
                  {quoteData.agreement.includeWeatherPolicy && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={weatherPolicyAccepted}
                        onChange={(e) => setWeatherPolicyAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Weather Policy</span>
                        <p className="text-xs text-gray-500">I understand services may be rescheduled due to weather</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Signature Section */}
            <div className="bg-white rounded-2xl shadow-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-4">Your Signature</h4>
              
              {/* Signer Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              {/* Signature Pad */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sign Below <span className="text-red-500">*</span>
                  </label>
                  {hasSignature && (
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <Eraser className="w-4 h-4" />
                      Clear
                    </button>
                  )}
                </div>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 relative"
                  style={{ touchAction: 'none' }}
                >
                  <canvas
                    ref={canvasRef}
                    className="w-full h-32 cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-gray-400 text-sm">Draw your signature here</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use your finger or mouse to sign
                </p>
              </div>

              {/* Agreement Acceptance */}
              <label className="flex items-start gap-3 cursor-pointer p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                <input
                  type="checkbox"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                  className="mt-1 h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  required
                />
                <div>
                  <span className="text-sm font-medium text-purple-900">
                    I have read and agree to the service agreement above
                  </span>
                  <p className="text-xs text-purple-700 mt-1">
                    By checking this box and signing, you agree to the terms and conditions outlined in the service agreement.
                  </p>
                </div>
              </label>
            </div>

            {/* Summary of what they're agreeing to */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p>📅 Service Date: <strong>{formatDate(selectedDate)}</strong></p>
                {timePreference && <p>🕐 Time: <strong>{timePreference}</strong></p>}
                <p>📍 Location: <strong>{quoteData.address}</strong></p>
                <p>💰 Total: <strong style={{ color: brandColor }}>{formatCurrency(quoteData.totalAmount)}</strong></p>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900">Unable to Submit</p>
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleBackToDate}
                className="flex-1 py-4 px-6 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                type="submit"
                disabled={submitting || !agreementAccepted || !hasSignature || !signerName.trim()}
                className="flex-[2] py-4 px-6 rounded-xl font-bold text-lg text-white transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg flex items-center justify-center gap-2"
                style={{ 
                  background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)`,
                }}
              >
                {submitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Confirm & Schedule
                  </>
                )}
              </button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-gray-500 text-sm">
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Secure & Encrypted
              </span>
              <span className="flex items-center gap-1">
                <FileSignature className="w-4 h-4" />
                Legally Binding
              </span>
            </div>
          </form>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-gray-500 text-sm">
        <p>Powered by <a href="https://suddyflow.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener">SuddyFlow</a></p>
      </footer>
    </div>
  );
};

export default QuoteAcceptForm;